// A fase da sessão de estudo, no precedente de `inscritosVocabulary.fase`.
// Progresso, baralho, controles, painel e conclusão leem o valor pronto.
// Nenhum deles compara `indice` com `fila.length` — essa comparação vive
// só em `faseDaSessao` (design.md, Decisão 4).
//
// A sessão não é persistida (Decisão 3): estes objetos nascem na memória
// quando a fila é lida e somem com o componente. Nada daqui escreve
// `localStorage`.

import { chaveUnidade } from '../../app/components/Revisao/oclusaoEstudo';
import type { DueCounts, Rating, ReviewCard } from '../../sdk/@types/Revisao';

export type FaseSessao = 'carregando' | 'vazia' | 'falhou' | 'estudando' | 'concluida';

/** Como terminou a leitura da fila. Independente do tamanho dela. */
export type LeituraFila = 'carregando' | 'pronta' | 'falhou';

/**
 * Uma nota que esta sessão registrou. `desfeita` impede um segundo desfazer
 * da mesma nota — a API também recusa encadear, e a tela não oferece.
 */
export interface NotaRegistrada {
  cardId: string;
  rating: Rating;
  elapsedMs: number;
  /** Epoch ms em que a nota foi gravada. */
  em: number;
  desfeita: boolean;
}

export interface SessaoEstado {
  leitura: LeituraFila;
  fila: ReviewCard[];
  indice: number;
  notas: NotaRegistrada[];
  /** Verdadeiro enquanto o POST da nota não voltou. Uma segunda nota nesse intervalo não grava. */
  registrando: boolean;
}

export interface ProgressoSessao {
  /** 1-based enquanto estuda. Na conclusão, o total. Fora isso, 0. */
  posicao: number;
  total: number;
  faltam: number;
}

/**
 * `POST /review/cards/:id/undo` ainda não está em `routes.go`. O cliente já
 * tem `ReviewService.undo`, mas sem a rota o comando não é oferecido e a
 * fila não anda para trás no lugar dele (Decisão 5). Vira `true` só quando
 * essa rota existir de verdade.
 */
export const API_OFERECE_DESFAZER = false;

export const CONTAGENS_VAZIAS: DueCounts = { new: 0, learning: 0, review: 0 };

export const SESSAO_INICIAL: SessaoEstado = {
  leitura: 'carregando',
  fila: [],
  indice: 0,
  notas: [],
  registrando: false,
};

/**
 * Duas linhas do mesmo modelo que perguntam marcações diferentes ficam as
 * duas. A mesma marcação — ou uma linha que não nomeia nenhuma — no mesmo
 * modelo é a corrida que materializa o cartão duas vezes: fica a primeira.
 * Esta função não parte uma linha em várias.
 */
export function dedupeQueue(cards: readonly ReviewCard[]): ReviewCard[] {
  const seen = new Set<string>();
  const out: ReviewCard[] = [];
  for (const card of cards) {
    const key = chaveUnidade(card);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(card);
  }
  return out;
}

/**
 * A única comparação de índice contra o tamanho da fila.
 *
 * Uma leitura rejeitada é `'falhou'` mesmo com a fila vazia. O vazio só
 * existe depois de uma leitura que deu certo e não trouxe cartão. Inverter
 * estas duas linhas faz a tela dizer "não há cartões" quando a rede caiu.
 */
export function faseDaSessao(estado: Pick<SessaoEstado, 'leitura' | 'fila' | 'indice'>): FaseSessao {
  if (estado.leitura === 'carregando') return 'carregando';
  if (estado.leitura === 'falhou') return 'falhou';
  if (estado.fila.length === 0) return 'vazia';
  if (estado.indice >= estado.fila.length) return 'concluida';
  return 'estudando';
}

export function filaCarregada(cards: readonly ReviewCard[] | null | undefined): SessaoEstado {
  return {
    leitura: 'pronta',
    fila: dedupeQueue(cards ?? []),
    indice: 0,
    notas: [],
    registrando: false,
  };
}

/**
 * Fila explícita, na ordem dada. Não passa por `dedupeQueue`: dois ids que
 * compartilham `templateId` são dois cartões que a conclusão nomeou, e
 * colapsar um deles tiraria da fila um cartão pedido.
 */
export function filaExata(cards: readonly ReviewCard[]): SessaoEstado {
  return {
    leitura: 'pronta',
    fila: cards.slice(),
    indice: 0,
    notas: [],
    registrando: false,
  };
}

/** Fila vazia de propósito: a fase sai `'falhou'`, nunca `'vazia'`. */
export function filaRejeitada(): SessaoEstado {
  return {
    leitura: 'falhou',
    fila: [],
    indice: 0,
    notas: [],
    registrando: false,
  };
}

export function cartaoCorrente(estado: SessaoEstado): ReviewCard | undefined {
  if (faseDaSessao(estado) !== 'estudando') return undefined;
  return estado.fila[estado.indice];
}

export function progresso(estado: SessaoEstado): ProgressoSessao {
  const total = estado.fila.length;
  const fase = faseDaSessao(estado);
  if (fase === 'concluida') return { posicao: total, total, faltam: 0 };
  if (fase !== 'estudando') return { posicao: 0, total, faltam: 0 };
  const posicao = estado.indice + 1;
  return { posicao, total, faltam: total - posicao };
}

function notaVigente(notas: readonly NotaRegistrada[], cardId: string): NotaRegistrada | undefined {
  for (let i = notas.length - 1; i >= 0; i--) {
    const nota = notas[i];
    if (nota.cardId === cardId && !nota.desfeita) return nota;
  }
  return undefined;
}

function ultimaNotaDesfazivel(notas: readonly NotaRegistrada[]): NotaRegistrada | undefined {
  const ultima = notas[notas.length - 1];
  if (!ultima || ultima.desfeita) return undefined;
  return ultima;
}

/**
 * Desfazer só existe com as duas condições juntas: a API tem a rota, e há
 * uma última nota ainda não desfeita. No primeiro cartão `notas` está
 * vazio, então o comando não aparece — mesmo que a rota exista.
 */
export function podeDesfazer(
  notas: readonly NotaRegistrada[],
  apiOfereceDesfazer: boolean,
  registrando = false
): boolean {
  if (registrando || !apiOfereceDesfazer) return false;
  return ultimaNotaDesfazivel(notas) !== undefined;
}

/**
 * Voltar um cartão na fila, sem desfazer nota. Recusar quando o cartão de
 * destino tem uma nota vigente: recuar o índice ali simularia o desfazer e
 * deixaria o agendamento alterado.
 */
export function podeRecuar(estado: SessaoEstado): boolean {
  if (estado.registrando || estado.indice <= 0) return false;
  const fase = faseDaSessao(estado);
  if (fase !== 'estudando' && fase !== 'concluida') return false;
  const card = estado.fila[estado.indice - 1];
  if (!card) return false;
  return notaVigente(estado.notas, card.id) === undefined;
}

/**
 * Trava o registro. `null` significa "ignore este acionamento" — já há um
 * POST em curso, ou não há cartão corrente. O chamador não dispara o HTTP.
 */
export function iniciarRegistro(estado: SessaoEstado): SessaoEstado | null {
  if (estado.registrando) return null;
  if (faseDaSessao(estado) !== 'estudando') return null;
  if (!estado.fila[estado.indice]) return null;
  return { ...estado, registrando: true };
}

/** A nota entrou. Avança um cartão. No último, a fase passa a `'concluida'`. */
export function concluirRegistro(
  estado: SessaoEstado,
  rating: Rating,
  elapsedMs: number,
  em: number
): SessaoEstado {
  const card = estado.fila[estado.indice];
  if (!card) return { ...estado, registrando: false };
  const nota: NotaRegistrada = {
    cardId: card.id,
    rating,
    elapsedMs,
    em,
    desfeita: false,
  };
  return {
    ...estado,
    notas: [...estado.notas, nota],
    indice: estado.indice + 1,
    registrando: false,
  };
}

/** O POST falhou. A fila e as notas ficam onde estavam. */
export function cancelarRegistro(estado: SessaoEstado): SessaoEstado {
  if (!estado.registrando) return estado;
  return { ...estado, registrando: false };
}

/**
 * Avança para o próximo cartão sem escrever agendamento. O objeto do cartão
 * permanece na mesma posição de `fila` — pular não o tira da fila do dia e
 * não chama a rota de resposta. No último cartão o índice passa do fim e a
 * fase fica `'concluida'`; o cartão pulado continua dentro de `fila`.
 */
export function pularCartao(estado: SessaoEstado): SessaoEstado {
  if (estado.registrando) return estado;
  if (faseDaSessao(estado) !== 'estudando') return estado;
  return { ...estado, indice: estado.indice + 1 };
}

export function recuarCartao(estado: SessaoEstado): SessaoEstado {
  if (!podeRecuar(estado)) return estado;
  return { ...estado, indice: estado.indice - 1 };
}

/**
 * Troca, na fila, o cartão de mesmo id pelo conteúdo que o PUT aceitou.
 * O índice não muda: a revisão continua no mesmo ponto. Id ausente da fila
 * devolve o mesmo estado.
 */
export function substituirCartao(estado: SessaoEstado, cartao: ReviewCard): SessaoEstado {
  if (!cartao?.id) return estado;
  const pos = estado.fila.findIndex((card) => card.id === cartao.id);
  if (pos < 0) return estado;
  const fila = estado.fila.slice();
  fila[pos] = cartao;
  return { ...estado, fila };
}

/**
 * Aplica o cartão que a rota de desfazer devolveu. Sem a rota — ou sem uma
 * nota desfazível — devolve o mesmo estado, sem mexer no índice.
 */
export function aplicarDesfazer(
  estado: SessaoEstado,
  cartaoRestaurado: ReviewCard,
  apiOfereceDesfazer: boolean
): SessaoEstado {
  if (!podeDesfazer(estado.notas, apiOfereceDesfazer, estado.registrando)) return estado;
  const ultima = ultimaNotaDesfazivel(estado.notas);
  if (!ultima) return estado;
  if (!cartaoRestaurado || cartaoRestaurado.id !== ultima.cardId) return estado;
  const pos = estado.fila.findIndex((card) => card.id === ultima.cardId);
  if (pos < 0) return estado;
  const fila = estado.fila.slice();
  fila[pos] = cartaoRestaurado;
  const notas = estado.notas.slice();
  notas[notas.length - 1] = { ...ultima, desfeita: true };
  return { ...estado, fila, notas, indice: pos, registrando: false };
}

/** O que a tela lê. Os comandos são do hook; a fase já vem resolvida. */
export interface RevisaoSessaoVista {
  fase: FaseSessao;
  fila: readonly ReviewCard[];
  indice: number;
  cartao: ReviewCard | undefined;
  notas: readonly NotaRegistrada[];
  contagens: DueCounts;
  posicao: number;
  total: number;
  faltam: number;
  registrando: boolean;
  podeDesfazer: boolean;
  podeRecuar: boolean;
  registrar: (rating: Rating) => void;
  pular: () => void;
  desfazer: () => void;
  recuar: () => void;
  /** Põe na fila o cartão que um PUT aceito devolveu à tela. Não muda o índice. */
  atualizarCartao: (cartao: ReviewCard) => void;
  tentarDeNovo: () => void;
  /** Epoch ms da leitura que montou a fila. `null` enquanto carrega ou se falhou. */
  iniciadaEm: number | null;
  /** Epoch ms em que o cartão corrente apareceu. `null` fora de `'estudando'`. */
  cartaoMostradoEm: number | null;
}
